/**
 * Toast Context
 * Global toast notification system using Radix UI Toast
 *
 * Uses React state with useCallback pattern to survive StrictMode and HMR.
 *
 * Usage:
 * ```tsx
 * const { toast } = useToast();
 *
 * toast.success('Project created successfully!');
 * toast.error('Failed to save changes');
 * toast.info('Processing your request...');
 * ```
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { ToastProvider, ToastViewport, Toast } from '@/components/ui/toast';

export interface ToastMessage {
  id: string;
  variant: 'success' | 'error' | 'info';
  title?: string;
  description?: string;
  duration?: number;
}

interface ToastContextValue {
  toast: {
    success: (title: string, description?: string, duration?: number) => void;
    error: (title: string, description?: string, duration?: number) => void;
    info: (title: string, description?: string, duration?: number) => void;
  };
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const MAX_TOASTS = 5;

export function ToastContextProvider({ children }: { children: React.ReactNode }) {
  // Use React state for toasts - survives HMR properly
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Use ref for timers to persist across renders
  const timersRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const addToast = useCallback(
    (
      variant: 'success' | 'error' | 'info',
      title: string,
      description?: string,
      duration = 5000
    ): void => {
      // Use crypto.randomUUID() for guaranteed unique IDs
      const id = crypto.randomUUID();
      const newToast: ToastMessage = { id, variant, title, description, duration };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        // Keep only last N toasts to prevent performance issues
        return updated.slice(-MAX_TOASTS);
      });

      // Auto-dismiss after duration
      const timerId = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        delete timersRef.current[id];
      }, duration);

      timersRef.current[id] = timerId;
    },
    []
  );

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  // Memoize toast methods - stable references
  const toastMethods = useMemo(
    () => ({
      success: (title: string, description?: string, duration?: number) => {
        addToast('success', title, description, duration);
      },
      error: (title: string, description?: string, duration?: number) => {
        addToast('error', title, description, duration);
      },
      info: (title: string, description?: string, duration?: number) => {
        addToast('info', title, description, duration);
      },
    }),
    [addToast]
  );

  const contextValue: ToastContextValue = useMemo(
    () => ({
      toast: toastMethods,
      dismiss,
    }),
    [toastMethods, dismiss]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      <ToastProvider swipeDirection="right" duration={5000}>
        {children}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            variant={toast.variant}
            title={toast.title}
            description={toast.description}
            duration={toast.duration}
            onClose={() => dismiss(toast.id)}
          />
        ))}
        <ToastViewport position="bottom-right" />
      </ToastProvider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastContextProvider');
  }
  return context;
}
